import { useCallback, type ReactNode } from 'react';
import { Box, Text } from '@/components/primitives';
import { wrapTextChild } from '@/components/primitives/wrapTextChild';
import { StyledDialog } from '../StyledDialog';
import type { DialogSize } from '@/components/templates';

export interface SettingsDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog should close */
  onClose: () => void;
  /** Dialog title */
  title?: string;
  /** Optional subtitle below title */
  subtitle?: string;
  /** Dialog content */
  children: ReactNode;
  /** Max width of dialog (default: 'sm') */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Opt-in: body is a flex column so one child can be the only scroller (see StyledDialog `fitBody`). */
  fitBody?: boolean;
}

// Map MUI maxWidth to our DialogSize
const maxWidthToSize: Record<string, DialogSize> = {
  xs: 'compact',
  sm: 'standard',
  md: 'form',
  lg: 'large',
  xl: 'fullWidth',
};

/**
 * SettingsDialog - Reusable modal for settings/info screens
 *
 * Provides consistent styling across all settings-type modals:
 * - Close button in top-right corner
 * - Optional title and subtitle
 * - Scrollable content area
 *
 * Uses StyledDialog with clean appearance
 *
 * Used by: ProfileEditDialog, OrganizationDialog
 */
export function SettingsDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = 'sm',
  fitBody,
}: SettingsDialogProps) {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      size={maxWidthToSize[maxWidth] || 'standard'}
      // Pass title to StyledDialog so it becomes the accessible Dialog.Title
      // (aria-labelledby target). Body-only title left SR announcing "Dialog".
      title={title}
      footer={null}
      fitBody={fitBody}
    >
      {subtitle && (
        <Box marginBottom="md">
          <Text color="secondary">
            {subtitle}
          </Text>
        </Box>
      )}

      {wrapTextChild(children)}
    </StyledDialog>
  );
}
